import SelectField from './SelectField';
import type { SelectOption } from './types';

type HelpSupportSectionProps = {
  data: {
    isQueryOpen: boolean;
    category: string;
    query: string;
    isSubmitting: boolean;
    errorMessage: string;
    supportEmail: string;
    categoryOptions: SelectOption[];
    faqs: { question: string; answer: string }[];
    openFaqIndex: number | null;
    isEmailCopied: boolean;
  };
  actions: {
    onOpenQuery: () => void;
    onCategoryChange: (value: string) => void;
    onQueryChange: (value: string) => void;
    onCancelQuery: () => void;
    onSubmitQuery: () => void;
    onToggleFaq: (index: number) => void;
    onCopyEmail: () => void;
  };
};

const HelpSupportSection = ({ data, actions }: HelpSupportSectionProps) => {
  const {
    isQueryOpen,
    category,
    query,
    isSubmitting,
    errorMessage,
    supportEmail,
    categoryOptions,
    faqs,
    openFaqIndex,
    isEmailCopied,
  } = data;
  const {
    onOpenQuery,
    onCategoryChange,
    onQueryChange,
    onCancelQuery,
    onSubmitQuery,
    onToggleFaq,
    onCopyEmail,
  } = actions;

  return (
  <div className="flex flex-col gap-6">
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">Contact Support</p>
          <p className="mt-1 text-sm text-slate-500">
            Please reach out to our support for any inquiries.
          </p>
        </div>
        {!isQueryOpen ? (
          <button
            type="button"
            className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
            onClick={onOpenQuery}
          >
            Ask Query
          </button>
        ) : null}
      </div>
      <div className="mt-4 border-b border-slate-200" />
    </div>

    {isQueryOpen ? (
      <div className="flex w-full flex-col gap-6">
        <div className="flex w-full max-w-[960px] flex-col gap-6">
          <label className="text-sm font-semibold text-slate-900">
            Select Category
          </label>
          <div className="mt-2">
            <SelectField
              value={category}
              options={categoryOptions}
              listHeader="Select Category"
              onChange={onCategoryChange}
            />
          </div>
        </div>
        <div className="w-full max-w-[960px]">
          <label className="text-sm font-semibold text-slate-900">Query</label>
          <textarea
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Write your Query here"
            rows={6}
            className="mt-2 w-full resize-none rounded-[12px] border border-slate-300 px-3 py-3 text-sm text-slate-600 outline-none focus:border-blue-600"
          />
        </div>
        {errorMessage ? (
          <p className="max-w-[960px] text-xs font-semibold text-rose-500">
            {errorMessage}
          </p>
        ) : null}
        <div className="w-full max-w-[960px]">
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
              onClick={onCancelQuery}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-[12px] bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={onSubmitQuery}
              disabled={!query.trim() || isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Query'}
            </button>
          </div>
        </div>
      </div>
    ) : null}

    <div className="w-full max-w-[960px] rounded-[12px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">Email Support</p>
          <p className="mt-1 text-sm text-slate-500">{supportEmail}</p>
        </div>
        <button
          type="button"
          onClick={onCopyEmail}
          className="inline-flex items-center gap-2 rounded-[10px] border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
        >
          <span className="inline-flex h-4 w-4 items-center justify-center rounded border border-slate-300">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-3 w-3"
              aria-hidden="true"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </span>
          {isEmailCopied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>

    <div className="w-full max-w-[960px] rounded-[12px] border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-900">
        Frequently Asked Questions
      </p>
      <div className="mt-3 flex flex-col divide-y divide-slate-200">
        {faqs.map((faq, index) => {
          const isOpen = openFaqIndex === index;
          return (
            <div key={faq.question} className="py-3">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-4 text-left text-sm font-semibold text-slate-700"
                onClick={() => onToggleFaq(index)}
              >
                <span>{faq.question}</span>
                <span className="flex h-6 w-6 items-center justify-center text-slate-400">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    {isOpen ? <path d="M5 12h14" /> : <path d="M12 5v14M5 12h14" />}
                  </svg>
                </span>
              </button>
              {isOpen ? (
                <p className="mt-3 text-sm text-slate-500">{faq.answer}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  </div>
  );
};

export default HelpSupportSection;
